import { Apartment } from "@boark/shared/types/apartment";
import { ApartmentWithHoldingsAndLeasesAndSublets } from "@boark/shared/types/apartment-with-holdings-and-leases-and-sublets";
import { Holding } from "@boark/shared/types/holding";
import { Lease } from "@boark/shared/types/lease";
import { Sublet } from "@boark/shared/types/sublet";
import { formatResidencyName } from "@boark/shared/utils/apartment-residency";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FileList } from "../../components/file-list";
import { H1 } from "../../components/h1";
import { H2 } from "../../components/h2";
import { Modal } from "../../components/modal";
import { OwnerDropdown } from "../../components/owner-dropdown";
import { Column, Table } from "../../components/table";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetApartment } from "../../queries/get-apartment";
import { useGetAssociation } from "../../queries/get-association";
import { useGetAssociationContext } from "../../queries/get-association-context";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import { getId } from "../../utils/id";
import { isCurrent } from "../../utils/is-current";
import { navigateBack } from "../../utils/navigate-back";

type HoldingData = Holding & {
  state: "active" | "sublet" | "ended";
};

type LeaseData = Lease & {
  state: "active" | "sublet" | "ended";
};

type SubletData = Sublet & {
  isSublet: boolean;
  active: boolean;
};

export function Apartment() {
  const navigate = useNavigate();
  const { id } = useParams();
  if (!id) {
    throw new Error("Apartment ID is required");
  }

  const { data: apartment } = useGetApartment(id);
  const { data: associationContext } = useGetAssociationContext();

  if (!associationContext?.id) {
    throw new Error("Association ID is required");
  }

  const { data: association } = useGetAssociation(getId(associationContext.id));
  const categories = useMemo(() => {
    return association?.entityFileTypes?.map((entityFileType) => ({
      name: entityFileType.name,
      label: entityFileType.label,
    }));
  }, [association]);
  const { locale: t, get: getLocale } = useLocale();

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/apartment");
  };

  const holdings = useMemo(() => {
    return (
      apartment?.holdings.map((holding) => {
        const sublets = holding.sublets.map((sublet) => ({
          ...sublet,
          active: isCurrent(sublet),
          isSublet: true,
        }));
        const hasActiveSublets = sublets.some((sublet) => sublet.active);
        let state: "active" | "sublet" | "ended";
        if (hasActiveSublets) {
          state = "sublet";
        } else if (!isCurrent(holding)) {
          state = "ended";
        } else {
          state = "active";
        }
        return {
          ...holding,
          state,
          sublets,
        };
      }) ?? []
    );
  }, [apartment]);

  const leases = useMemo(() => {
    return (
      apartment?.leases.map((lease) => {
        const sublets = lease.sublets.map((sublet) => ({
          ...sublet,
          active: isCurrent(sublet),
          isSublet: true,
        }));
        const hasActiveSublets = sublets.some((sublet) => sublet.active);
        let state: "active" | "sublet" | "ended";
        if (hasActiveSublets) {
          state = "sublet";
        } else if (!isCurrent(lease)) {
          state = "ended";
        } else {
          state = "active";
        }
        return {
          ...lease,
          state,
          sublets,
        };
      }) ?? []
    );
  }, [apartment]);

  return (
    <Modal open onClose={() => closeModal()}>
      <div className="flex flex-col gap-12">
        <div>
          {apartment && (
            <>
              <H1>
                #{apartment.index} - {apartment.objectNumber}{" "}
                {apartment.streetNumber}:{apartment.apartmentNumber}
              </H1>
              <ApartmentContent apartment={apartment} />
            </>
          )}
        </div>
        <FileList categories={categories} rootPath={`apartment/${id}`} />
        {holdings.length > 0 && (
          <div>
            <H2 className="mb-2">{getLocale("holding.holdings")}</H2>
            <Table
              data={holdings}
              sortable={false}
              sortBy="fromDate"
              sortByDirection="desc"
              subListBy="sublets"
            >
              <Column
                body={(row: HoldingData | SubletData) => (
                  <div className="flex gap-2 justify-between whitespace-nowrap">
                    {"owners" in row && (
                      <>
                        {formatResidencyName(row)}
                        <OwnerDropdown holding={row} />
                      </>
                    )}
                    {"tenants" in row && (
                      <>
                        {formatResidencyName(row)}
                        <OwnerDropdown showSubletPrefix={false} sublet={row} />
                      </>
                    )}
                  </div>
                )}
                field="residents"
              >
                {getLocale("holding.residents")}
              </Column>
              <Column
                body={(row: HoldingData) => formatDate(row.fromDate)}
                field="fromDate"
              >
                {getLocale("holding.fromDate")}
              </Column>
              <Column
                body={(row: HoldingData) =>
                  (row.toDate && formatDate(row.toDate)) ?? "-"
                }
                field="toDate"
              >
                {getLocale("holding.toDate")}
              </Column>
              <Column
                body={(row: LeaseData | SubletData) => {
                  if ("state" in row) {
                    return getLocale(`holding.state.${row.state}`);
                  } else if ("active" in row) {
                    return row.active ? t.yes : t.no;
                  }
                  return "-";
                }}
                field="state"
              >
                {getLocale("holding.state.state")} /{" "}
                {getLocale("sublet.active")}
              </Column>
            </Table>
          </div>
        )}
        {leases.length > 0 && (
          <div>
            <H2 className="mb-2">{getLocale("lease.leases")}</H2>
            <Table
              data={leases}
              sortable={false}
              sortBy="fromDate"
              sortByDirection="desc"
              subListBy="sublets"
            >
              <Column
                body={(row: LeaseData | SubletData) => (
                  <div className="flex gap-2 justify-between whitespace-nowrap">
                    {formatResidencyName(row)}
                    <OwnerDropdown lease={row as LeaseData} />
                  </div>
                )}
                field="residents"
              >
                {getLocale("lease.residents")}
              </Column>
              <Column
                body={(row: LeaseData) => formatDate(row.fromDate)}
                field="fromDate"
              >
                {getLocale("lease.fromDate")}
              </Column>
              <Column
                body={(row: LeaseData) =>
                  (row.toDate && formatDate(row.toDate)) ?? "-"
                }
                field="toDate"
              >
                {getLocale("lease.toDate")}
              </Column>
              <Column
                body={(row: LeaseData | SubletData) => {
                  if ("state" in row) {
                    return getLocale(`lease.state.${row.state}`);
                  } else if ("active" in row) {
                    return row.active ? t.yes : t.no;
                  }
                  return "-";
                }}
                field="state"
              >
                {getLocale("lease.state.state")} / {getLocale("sublet.active")}
              </Column>
            </Table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ApartmentContent({
  apartment,
}: {
  apartment: ApartmentWithHoldingsAndLeasesAndSublets;
}) {
  const { get: getLocale } = useLocale();

  const { data: additionalFields } = useAdditionalFields("apartment");

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <ApartmentField
          label={getLocale("apartment.objectNumber")}
          value={apartment.objectNumber}
        />
        <ApartmentField
          label={getLocale("apartment.streetNumber")}
          value={apartment.streetNumber}
        />
        <ApartmentField
          label={getLocale("apartment.apartmentNumber")}
          value={apartment.apartmentNumber}
        />
        <ApartmentField
          label={getLocale("apartment.floorNumber")}
          value={apartment.floorNumber}
        />
        <ApartmentField
          label={getLocale("apartment.area")}
          value={apartment.area ? `${apartment.area}m²` : undefined}
        />
        <ApartmentField
          label={getLocale("apartment.share")}
          value={apartment.share ? `${apartment.share}%` : undefined}
        />
        {additionalFields.length > 0 &&
          additionalFields.map((field) => {
            let value = apartment.additionalData?.[field.name];
            if (field.type === "select") {
              value = field.options?.find(
                (option) =>
                  option.value === apartment.additionalData?.[field.name],
              )?.label;
            }

            return (
              <ApartmentField
                key={field.name}
                label={field.label}
                value={value as React.ReactNode}
              />
            );
          })}
      </div>
    </div>
  );
}

function ApartmentField({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) {
  const { locale: t } = useLocale();

  let displayValue: React.ReactNode = value !== undefined ? value : "-";

  if (value instanceof Date) {
    displayValue = formatDate(value);
  } else if (typeof value === "boolean") {
    displayValue = value ? t.yes : t.no;
  }

  return (
    <div className="flex gap-2">
      <span className="font-bold">{label}:</span>
      <span>{displayValue}</span>
    </div>
  );
}
